if (!customElements.get('brand-directory')) {
  customElements.define(
    'brand-directory',
    class BrandDirectory extends HTMLElement {
      constructor() {
        super();
        this.onTabClick = this.onTabClick.bind(this);
        this.onSearchInput = this.onSearchInput.bind(this);
        this.onAzLetterClick = this.onAzLetterClick.bind(this);
        this.onAzScrollSpy = this.onAzScrollSpy.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
      }

      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[data-brand-tab]')];
        this.panels = [...this.querySelectorAll('[data-brand-panel]')];
        this.searchInput = this.querySelector('[data-brand-search]');
        this.noResultsEl = this.querySelector('[data-brand-no-results]');
        this.azNavWrap = this.querySelector('[data-brand-az-nav-wrap]');
        this.azToolbar = this.querySelector('[data-brand-az-toolbar]');
        this.azLetters = [...this.querySelectorAll('[data-brand-az-letter]')];
        this.allPanel = this.panels.find((panel) => panel.dataset.brandPanel === 'all');

        this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
        this.searchInput?.addEventListener('input', this.onSearchInput);
        this.azLetters.forEach((btn) => btn.addEventListener('click', this.onAzLetterClick));

        this.setupAzSticky();
        this.setupAzScrollSpy();

        const initial = this.tabs.find((t) => t.classList.contains('is-active'))?.dataset.brandTab || 'all';
        this.activateTab(initial, false);
      }

      disconnectedCallback() {
        this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
        this.searchInput?.removeEventListener('input', this.onSearchInput);
        this.azLetters?.forEach((btn) => btn.removeEventListener('click', this.onAzLetterClick));
        window.removeEventListener('scroll', this.onWindowScroll);
        window.removeEventListener('resize', this.onWindowResize);
        this.azObserver?.disconnect();
        this.unpinAzToolbar(true);
      }

      onTabClick(event) {
        event.preventDefault();
        const tabId = event.currentTarget.dataset.brandTab;
        if (tabId) this.activateTab(tabId, true);
      }

      onSearchInput() {
        this.filterBrands(this.searchInput?.value || '');
      }

      onAzLetterClick(event) {
        const btn = event.currentTarget;
        if (btn.disabled || btn.classList.contains('is-disabled')) return;
        const letter = btn.dataset.brandAzLetter;
        if (letter) this.scrollToLetter(letter);
      }

      onWindowScroll() {
        this.updateAzSticky();
      }

      onWindowResize() {
        if (!this.azNavWrap?.classList.contains('is-pinned')) {
          this.measureAzToolbarOffset();
        }
        this.updateAzSticky();
        this.updateAzScrollSpyMargin();
      }

      activateTab(tabId, clearSearch) {
        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.brandTab === tabId;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        this.panels.forEach((panel) => {
          const show = panel.dataset.brandPanel === tabId;
          panel.hidden = !show;
        });

        if (clearSearch && this.searchInput) {
          this.searchInput.value = '';
        }

        this.updateAzNavVisibility(tabId);
        this.filterBrands(this.searchInput?.value || '');

        if (tabId !== 'all') {
          this.unpinAzToolbar(true);
        } else {
          requestAnimationFrame(() => {
            this.measureAzToolbarOffset();
            this.updateAzSticky();
          });
        }
      }

      getActivePanel() {
        return this.panels.find((panel) => !panel.hidden);
      }

      filterBrands(query) {
        const panel = this.getActivePanel();
        if (!panel) return;

        const normalized = query.trim().toLowerCase();
        let visibleCount = 0;

        panel.querySelectorAll('[data-brand-card]').forEach((card) => {
          const name = (card.dataset.brandName || '').toLowerCase();
          const match = !normalized || name.includes(normalized);
          card.classList.toggle('is-hidden', !match);
          card.toggleAttribute('data-hidden', !match);
          if (match) visibleCount += 1;
        });

        panel.querySelectorAll('[data-brand-letter-group]').forEach((group) => {
          if (group.hasAttribute('data-letter-empty')) {
            group.classList.add('is-hidden');
            group.setAttribute('data-hidden', 'true');
            group.hidden = true;
            return;
          }

          const cards = group.querySelectorAll('[data-brand-card]');
          if (cards.length === 0) {
            group.classList.add('is-hidden');
            group.setAttribute('data-hidden', 'true');
            group.hidden = true;
            return;
          }

          const visibleCards = group.querySelectorAll('[data-brand-card]:not(.is-hidden)');
          const hideGroup = visibleCards.length === 0;
          group.classList.toggle('is-hidden', hideGroup);
          group.toggleAttribute('data-hidden', hideGroup);
          group.hidden = hideGroup;
        });

        if (this.noResultsEl) {
          const showNoResults = normalized.length > 0 && visibleCount === 0;
          this.noResultsEl.hidden = !showNoResults;
        }

        if (panel === this.allPanel) {
          this.syncAzLetterAvailability();
        }
      }

      updateAzNavVisibility(tabId) {
        if (!this.azNavWrap) return;

        const show =
          tabId === 'all' &&
          this.allPanel &&
          !this.allPanel.hidden &&
          this.allPanel.querySelector('[data-brand-letter-group]:not([data-letter-empty])');

        this.azNavWrap.hidden = !show;

        if (show) {
          this.syncAzLetterAvailability();
          requestAnimationFrame(() => {
            this.measureAzToolbarOffset();
            this.updateAzSticky();
          });
        } else {
          this.unpinAzToolbar(true);
        }
      }

      getAvailableLetters() {
        if (!this.allPanel) return new Set();

        const available = new Set();
        this.allPanel
          .querySelectorAll('[data-brand-letter-group]:not([data-letter-empty])')
          .forEach((group) => {
            if (group.hidden || group.classList.contains('is-hidden')) return;
            const letter = group.dataset.letter;
            if (letter) available.add(letter);
          });
        return available;
      }

      syncAzLetterAvailability() {
        if (!this.azLetters.length) return;

        const available = this.getAvailableLetters();
        let firstAvailable = null;

        this.azLetters.forEach((btn) => {
          const letter = btn.dataset.brandAzLetter;
          const hasBrands = available.has(letter);
          btn.disabled = !hasBrands;
          btn.classList.toggle('is-disabled', !hasBrands);
          if (hasBrands && !firstAvailable) {
            firstAvailable = letter;
          }
        });

        const activeBtn = this.azLetters.find((btn) => btn.classList.contains('is-active') && !btn.disabled);
        if (!activeBtn && firstAvailable) {
          this.setActiveAzLetter(firstAvailable, false);
        } else if (activeBtn?.disabled && firstAvailable) {
          this.setActiveAzLetter(firstAvailable, false);
        }
      }

      setActiveAzLetter(letter, scrollTabIntoView = true) {
        this.azLetters.forEach((btn) => {
          const active = btn.dataset.brandAzLetter === letter && !btn.disabled;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', active ? 'true' : 'false');
          btn.tabIndex = active ? 0 : -1;
        });

        if (scrollTabIntoView) {
          this.scrollActiveAzLetterIntoView();
        }
      }

      scrollActiveAzLetterIntoView() {
        const activeBtn = this.azLetters.find((btn) => btn.classList.contains('is-active'));
        const scrollContainer = this.querySelector('.brand-directory__az-scroll');
        if (!activeBtn || !scrollContainer) return;
        activeBtn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      }

      getStickyTop() {
        const headerBottom = document.querySelector('header .header__bottom');
        if (headerBottom) {
          return headerBottom.getBoundingClientRect().bottom;
        }
        const stickyHeader = document.querySelector('header[is="sticky-header"], sticky-header');
        if (stickyHeader) {
          return stickyHeader.getBoundingClientRect().bottom;
        }
        const headerHeight = getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height')
          .trim();
        return parseFloat(headerHeight) || 0;
      }

      getAzBarHeight() {
        return this.azNavWrap?.offsetHeight || this.azToolbar?.offsetHeight || 52;
      }

      getScrollOffset() {
        return this.getStickyTop() + this.getAzBarHeight() + 12;
      }

      setupAzSticky() {
        if (!this.azNavWrap) return;

        this.azPlaceholder = document.createElement('div');
        this.azPlaceholder.className = 'brand-directory__az-placeholder';
        this.azPlaceholder.hidden = true;
        this.azNavWrap.after(this.azPlaceholder);

        window.addEventListener('scroll', this.onWindowScroll, { passive: true });
        window.addEventListener('resize', this.onWindowResize);
      }

      measureAzToolbarOffset() {
        if (!this.azNavWrap || this.azNavWrap.hidden) return;

        const wasPinned = this.azNavWrap.classList.contains('is-pinned');
        if (wasPinned) {
          this.unpinAzToolbar(false);
        }

        const rect = this.azNavWrap.getBoundingClientRect();
        this.azToolbarOffset = rect.top + window.scrollY;
      }

      updateAzSticky() {
        if (!this.azNavWrap || this.azNavWrap.hidden) {
          this.unpinAzToolbar(true);
          return;
        }

        if (this.azToolbarOffset == null) {
          this.measureAzToolbarOffset();
        }

        const stickyTop = this.getStickyTop();
        const shouldPin = window.scrollY + stickyTop >= (this.azToolbarOffset ?? 0);

        if (shouldPin) {
          if (!this.azNavWrap.classList.contains('is-pinned')) {
            this.azPlaceholder.style.height = `${this.azNavWrap.offsetHeight}px`;
            this.azPlaceholder.hidden = false;
            this.azNavWrap.classList.add('is-pinned');
          }

          this.azNavWrap.style.setProperty('--brand-directory-az-sticky-top', `${stickyTop}px`);
          document.documentElement.style.setProperty(
            '--brand-directory-az-height',
            `${this.getAzBarHeight()}px`
          );
          return;
        }

        this.unpinAzToolbar(false);
      }

      unpinAzToolbar(resetOffset = false) {
        if (!this.azNavWrap) return;

        this.azNavWrap.classList.remove('is-pinned');
        this.azNavWrap.style.removeProperty('--brand-directory-az-sticky-top');

        if (this.azPlaceholder) {
          this.azPlaceholder.hidden = true;
          this.azPlaceholder.style.height = '0';
        }

        if (resetOffset) {
          this.azToolbarOffset = null;
        }
      }

      clearLetterFocus() {
        this.allPanel?.querySelectorAll('.is-letter-focused').forEach((el) => {
          el.classList.remove('is-letter-focused');
        });
      }

      focusLetterGroup(group) {
        this.clearLetterFocus();
        group.classList.add('is-letter-focused');

        const focusTarget = group.querySelector('.brand-directory__section-bar') || group;
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });

        window.setTimeout(() => {
          group.classList.remove('is-letter-focused');
        }, 2200);
      }

      scrollToLetter(letter) {
        if (!this.allPanel) return;

        const group = this.allPanel.querySelector(
          `[data-brand-letter-group][data-letter="${letter}"]:not([data-letter-empty])`
        );
        if (!group || group.hidden || group.classList.contains('is-hidden')) return;

        this.azScrollLock = true;
        this.setActiveAzLetter(letter);

        const offset = this.getScrollOffset();
        const top = group.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        const finishScroll = () => {
          this.updateAzSticky();
          this.focusLetterGroup(group);
          window.setTimeout(() => {
            this.azScrollLock = false;
          }, 120);
        };

        let scrollFinished = false;
        const finishOnce = () => {
          if (scrollFinished) return;
          scrollFinished = true;
          finishScroll();
        };

        if ('onscrollend' in window) {
          window.addEventListener('scrollend', finishOnce, { once: true });
          window.setTimeout(finishOnce, 900);
        } else {
          window.setTimeout(finishOnce, 700);
        }
      }

      getAzScrollSpyRootMargin() {
        const top = Math.round(this.getScrollOffset());
        return `-${top}px 0px -72% 0px`;
      }

      updateAzScrollSpyMargin() {
        if (!this.azObserver) return;
        this.azObserver.disconnect();
        this.setupAzScrollSpy();
      }

      setupAzScrollSpy() {
        if (!this.allPanel || typeof IntersectionObserver === 'undefined') return;

        this.azObserver = new IntersectionObserver(this.onAzScrollSpy, {
          root: null,
          rootMargin: this.getAzScrollSpyRootMargin(),
          threshold: 0,
        });

        this.allPanel.querySelectorAll('[data-brand-letter-group]:not([data-letter-empty])').forEach((group) => {
          this.azObserver.observe(group);
        });
      }

      onAzScrollSpy(entries) {
        if (this.azScrollLock) return;
        if (!this.azNavWrap || this.azNavWrap.hidden) return;
        if ((this.searchInput?.value || '').trim().length > 0) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        const topEntry = visible[0];
        if (!topEntry) return;

        const letter = topEntry.target.dataset.letter;
        if (letter) {
          this.setActiveAzLetter(letter, true);
        }
      }
    }
  );
}
